import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist'
import type { ComponentProps, MouseEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { documentsApi } from '@/lib/api/documents.api'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { formatFileSize } from '@/lib/utils/file-size'
import { downloadFileWithFeedback } from '@/lib/utils/download'
import { isTauri, openExternal } from '@/lib/utils/tauri'
import {
  ArrowSquareOutIcon,
  DownloadSimpleIcon,
  EyeIcon,
  FileTextIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import DOMPurify from 'dompurify'
import MarkdownIt from 'markdown-it'
import Papa from 'papaparse'
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url'
import { toast } from 'sonner'

import { DocumentTypeEnum } from '@scrambl/shared'

const logger = createLogger('components/shared/documents/DocumentPreviewDialog')

GlobalWorkerOptions.workerSrc = pdfWorkerUrl

export interface PreviewableDocument {
  id: string
  displayName: string
  type: DocumentTypeEnum
  storageKey?: string
  url?: string
  fileName?: string
  contentType?: string
  fileSize?: number
}

type SignedUrlResponse = { url: string; thumbnailUrl?: string }

type DocumentPreviewDialogProps = {
  document: PreviewableDocument | null
  open: boolean
  onOpenChange: (open: boolean) => void
  resolveSignedUrl?: (
    document: PreviewableDocument,
  ) => Promise<SignedUrlResponse>
}

type DocumentPreviewButtonProps = {
  document: PreviewableDocument
  resolveSignedUrl?: (
    document: PreviewableDocument,
  ) => Promise<SignedUrlResponse>
  className?: string
  buttonClassName?: string
  variant?: ComponentProps<typeof Button>['variant']
  size?: ComponentProps<typeof Button>['size']
  showLabel?: boolean
  label?: string
  disabled?: boolean
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
}

type PreviewKind =
  | 'url'
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'pptx'
  | 'archive'
  | 'text'
  | 'markdown'
  | 'html'
  | 'csv'
  | 'xlsx'
  | 'docx'
  | 'unsupported'

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string; url?: string }
  | {
      status: 'ready'
      kind: PreviewKind
      url?: string
      text?: string
      html?: string
      pdfData?: ArrayBuffer
      slides?: SlidePreview[]
      archiveEntries?: ArchiveEntryPreview[]
      csvRows?: string[][]
      sheetRows?: string[][]
      sheetName?: string
      unsupportedReason?: string
    }

const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
})

const TEXT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024
const TABLE_ROW_LIMIT = 80
const TABLE_COLUMN_LIMIT = 30
const PRESENTATION_SLIDE_LIMIT = 80
const ARCHIVE_ENTRY_LIMIT = 300
const IMAGE_EXTENSIONS = new Set([
  'avif',
  'bmp',
  'gif',
  'ico',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'tif',
  'tiff',
  'webp',
])
const AUDIO_EXTENSIONS = new Set([
  'aac',
  'flac',
  'm4a',
  'mp3',
  'oga',
  'ogg',
  'opus',
  'wav',
  'weba',
])
const VIDEO_EXTENSIONS = new Set([
  'avi',
  'm4v',
  'mkv',
  'mov',
  'mp4',
  'mpeg',
  'mpg',
  'ogv',
  'webm',
])
const TEXT_LIKE_EXTENSIONS = new Set([
  'bash',
  'cfg',
  'conf',
  'config',
  'css',
  'diff',
  'dockerignore',
  'editorconfig',
  'eml',
  'env',
  'example',
  'gitignore',
  'graphql',
  'gql',
  'htaccess',
  'http',
  'ics',
  'ini',
  'js',
  'json',
  'jsx',
  'less',
  'license',
  'lock',
  'log',
  'mjs',
  'properties',
  'patch',
  'rc',
  'scss',
  'sh',
  'sql',
  'srt',
  'toml',
  'ts',
  'tsx',
  'txt',
  'vtt',
  'xml',
  'yaml',
  'yml',
  'zsh',
])

type SlidePreview = {
  number: number
  title: string
  lines: string[]
}

type ArchiveEntryPreview = {
  name: string
  isDirectory: boolean
  size?: number
  modified?: Date
}
const TEXT_LIKE_FILENAMES = new Set([
  '.dockerignore',
  '.editorconfig',
  '.env',
  '.env.example',
  '.env.local',
  '.env.sample',
  '.eslintignore',
  '.gitignore',
  '.npmrc',
  '.prettierignore',
  'dockerfile',
  'license',
  'makefile',
  'readme',
])

function normalizeUrl(raw?: string): string {
  if (!raw) return ''
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) return raw
  if (raw.startsWith('//')) return `https:${raw}`
  return `https://${raw.replace(/^\/+/, '')}`
}

function getExtension(document: PreviewableDocument): string {
  const name = document.fileName || document.displayName || ''
  const cleanName = name.split('?')[0]?.split('#')[0] ?? ''
  const extension = cleanName.includes('.')
    ? cleanName.split('.').pop()?.toLowerCase()
    : ''
  return extension || ''
}

function getBaseName(document: PreviewableDocument): string {
  const name = document.fileName || document.displayName || ''
  const cleanName = name.split('?')[0]?.split('#')[0] ?? ''
  return cleanName.split('/').pop()?.toLowerCase() || ''
}

function isKnownTextFile(document: PreviewableDocument): boolean {
  const baseName = getBaseName(document)
  const extension = getExtension(document)

  return (
    TEXT_LIKE_FILENAMES.has(baseName) ||
    TEXT_LIKE_EXTENSIONS.has(extension) ||
    baseName.startsWith('.env.') ||
    baseName.startsWith('dockerfile')
  )
}

function getPreviewKind(document: PreviewableDocument): PreviewKind {
  if (document.type === DocumentTypeEnum.URL) return 'url'

  const contentType = (document.contentType || '').toLowerCase()
  const extension = getExtension(document)

  if (contentType.startsWith('image/') || IMAGE_EXTENSIONS.has(extension))
    return 'image'
  if (contentType.startsWith('video/') || VIDEO_EXTENSIONS.has(extension))
    return 'video'
  if (contentType.startsWith('audio/') || AUDIO_EXTENSIONS.has(extension))
    return 'audio'
  if (contentType === 'application/pdf' || extension === 'pdf') return 'pdf'

  if (extension === 'docx') return 'docx'
  if (
    ['pptx', 'pptm'].includes(extension) ||
    contentType.includes('presentationml')
  )
    return 'pptx'
  if (
    ['xlsx', 'xls', 'xlsm', 'xlsb', 'ods'].includes(extension) ||
    contentType.includes('spreadsheetml') ||
    contentType === 'application/vnd.ms-excel' ||
    contentType === 'application/vnd.oasis.opendocument.spreadsheet'
  ) {
    return 'xlsx'
  }
  if (
    ['zip', 'jar', 'epub', 'apk', 'ipa'].includes(extension) ||
    contentType.includes('zip')
  ) {
    return 'archive'
  }

  if (extension === 'csv' || contentType.includes('csv')) return 'csv'
  if (extension === 'tsv' || contentType.includes('tab-separated-values'))
    return 'csv'
  if (extension === 'md' || extension === 'markdown') return 'markdown'
  if (
    extension === 'html' ||
    extension === 'htm' ||
    contentType === 'text/html'
  )
    return 'html'
  if (
    contentType.startsWith('text/') ||
    contentType.includes('json') ||
    contentType.includes('xml') ||
    isKnownTextFile(document)
  ) {
    return 'text'
  }

  return 'unsupported'
}

async function fetchArrayBuffer(url: string, maxBytes?: number) {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Preview request failed with ${response.status}`)
  }

  const contentLength = Number(response.headers.get('content-length') || '0')
  if (maxBytes && contentLength > maxBytes) {
    throw new Error('This file is too large to preview as text')
  }

  const buffer = await response.arrayBuffer()
  if (maxBytes && buffer.byteLength > maxBytes) {
    throw new Error('This file is too large to preview as text')
  }
  return buffer
}

async function fetchText(url: string) {
  const buffer = await fetchArrayBuffer(url, TEXT_PREVIEW_MAX_BYTES)
  return decodeTextPreview(buffer)
}

function decodeTextPreview(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer)
  const sample = bytes.slice(0, Math.min(bytes.length, 4096))

  if (sample.includes(0)) {
    throw new Error('This file looks like binary data, not text.')
  }

  const text = new TextDecoder('utf-8').decode(buffer)
  const sampleText = text.slice(0, 4096)
  const replacementCount = sampleText.match(/\uFFFD/g)?.length ?? 0

  if (sampleText.length > 0 && replacementCount / sampleText.length > 0.05) {
    throw new Error('This file is not valid UTF-8 text.')
  }

  return text
}

function toDisplayValue(value: unknown): string {
  if (value == null) return ''
  if (value instanceof Date) return value.toLocaleDateString()
  if (typeof value === 'object') {
    const maybeText = value as { text?: unknown; result?: unknown }
    if (typeof maybeText.text === 'string') return maybeText.text
    if (maybeText.result != null) return String(maybeText.result)
  }
  return String(value)
}

async function parseXlsx(url: string) {
  const buffer = await fetchArrayBuffer(url)
  const XLSX = await import('xlsx')
  const workbook = XLSX.read(buffer, { cellDates: true, type: 'array' })
  const sheetName = workbook.SheetNames[0]

  if (!sheetName) {
    return { sheetName: 'Workbook', sheetRows: [] }
  }

  const worksheet = workbook.Sheets[sheetName]
  const rawRows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    blankrows: false,
    header: 1,
    raw: false,
  })
  const rows = rawRows
    .slice(0, TABLE_ROW_LIMIT)
    .map(row =>
      row.slice(0, TABLE_COLUMN_LIMIT).map(cell => toDisplayValue(cell)),
    )
    .filter(row => row.some(Boolean))

  return { sheetName, sheetRows: rows }
}

async function parseDocx(url: string) {
  const buffer = await fetchArrayBuffer(url)
  const mammoth = await import('mammoth')
  const result = await mammoth.convertToHtml({ arrayBuffer: buffer })
  return DOMPurify.sanitize(result.value)
}

async function loadZip(url: string) {
  const buffer = await fetchArrayBuffer(url)
  const JSZip = (await import('jszip')).default
  return JSZip.loadAsync(buffer)
}

function getSlideNumber(path: string) {
  return Number(path.match(/slide(\d+)\.xml$/)?.[1] || '0')
}

function extractXmlText(xml: string) {
  const document = new DOMParser().parseFromString(xml, 'application/xml')
  const textNodes = Array.from(document.getElementsByTagName('a:t'))
  return textNodes.map(node => node.textContent?.trim() || '').filter(Boolean)
}

async function parsePptx(url: string): Promise<SlidePreview[]> {
  const zip = await loadZip(url)
  const slideFiles = Object.values(zip.files)
    .filter(file => /^ppt\/slides\/slide\d+\.xml$/.test(file.name))
    .sort(
      (first, second) =>
        getSlideNumber(first.name) - getSlideNumber(second.name),
    )
    .slice(0, PRESENTATION_SLIDE_LIMIT)

  const slides = await Promise.all(
    slideFiles.map(async file => {
      const number = getSlideNumber(file.name)
      const lines = extractXmlText(await file.async('text'))
      return {
        number,
        title: lines[0] || `Slide ${number}`,
        lines,
      }
    }),
  )

  return slides
}

function getZipEntrySize(file: unknown) {
  const maybeFile = file as { _data?: { uncompressedSize?: number } }
  return maybeFile._data?.uncompressedSize
}

async function parseArchive(url: string): Promise<ArchiveEntryPreview[]> {
  const zip = await loadZip(url)
  return Object.values(zip.files)
    .sort((first, second) => first.name.localeCompare(second.name))
    .slice(0, ARCHIVE_ENTRY_LIMIT)
    .map(file => ({
      name: file.name,
      isDirectory: file.dir,
      size: file.dir ? undefined : getZipEntrySize(file),
      modified: file.date,
    }))
}

function parseDelimitedRows(text: string, document: PreviewableDocument) {
  const delimiter = getExtension(document) === 'tsv' ? '\t' : undefined
  const parsed = Papa.parse<string[]>(text, {
    delimiter,
    skipEmptyLines: 'greedy',
  })
  const data = Array.isArray(parsed.data) ? parsed.data : []
  return data
    .slice(0, TABLE_ROW_LIMIT)
    .map(row =>
      row.slice(0, TABLE_COLUMN_LIMIT).map(cell => String(cell ?? '')),
    )
}

function renderTable(rows: string[][]) {
  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No rows to preview.
      </div>
    )
  }

  return (
    <div className="min-w-full overflow-auto">
      <table className="w-full border-collapse text-sm">
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex === 0 ? 'bg-muted/60' : ''}>
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={cn(
                    'max-w-[260px] border border-border/70 px-3 py-2 align-top',
                    rowIndex === 0 && 'font-medium',
                  )}
                >
                  <div className="whitespace-pre-wrap break-words">{cell}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function renderSlides(slides: SlidePreview[]) {
  if (slides.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No slide text found.
      </div>
    )
  }

  return (
    <ScrollArea className="h-full bg-muted/20">
      <div className="space-y-3 p-4">
        {slides.map(slide => (
          <section
            key={slide.number}
            className="rounded-md border border-border/70 bg-background p-4 shadow-sm"
          >
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground">
                {slide.number}
              </div>
              <h3 className="min-w-0 truncate text-sm font-semibold">
                {slide.title}
              </h3>
            </div>
            {slide.lines.length > 0 ? (
              <div className="space-y-1 text-sm text-muted-foreground">
                {slide.lines.slice(0, 30).map((line, index) => (
                  <p key={`${slide.number}-${index}`} className="break-words">
                    {line}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                This slide has no readable text.
              </p>
            )}
          </section>
        ))}
      </div>
    </ScrollArea>
  )
}

function renderArchiveEntries(entries: ArchiveEntryPreview[]) {
  if (entries.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        This archive is empty.
      </div>
    )
  }

  return (
    <ScrollArea className="h-full">
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 bg-background">
          <tr className="border-b border-border/70 text-left text-xs uppercase text-muted-foreground">
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="w-28 px-4 py-2 font-medium">Size</th>
            <th className="w-40 px-4 py-2 font-medium">Modified</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => (
            <tr key={entry.name} className="border-b border-border/50">
              <td className="max-w-[520px] px-4 py-2">
                <div className="truncate font-mono text-xs" title={entry.name}>
                  {entry.isDirectory ? `${entry.name}/` : entry.name}
                </div>
              </td>
              <td className="px-4 py-2 text-xs text-muted-foreground">
                {entry.isDirectory ? 'Folder' : formatFileSize(entry.size)}
              </td>
              <td className="px-4 py-2 text-xs text-muted-foreground">
                {entry.modified ? entry.modified.toLocaleDateString() : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollArea>
  )
}

function PdfPageCanvas({
  pdf,
  pageNumber,
  availableWidth,
}: {
  pdf: PDFDocumentProxy
  pageNumber: number
  availableWidth: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isRendering, setIsRendering] = useState(true)

  useEffect(() => {
    let cancelled = false
    let renderTask: RenderTask | null = null

    async function renderPage() {
      setIsRendering(true)

      try {
        const page = await pdf.getPage(pageNumber)
        if (cancelled) return

        const baseViewport = page.getViewport({ scale: 1 })
        const targetWidth = Math.max(280, availableWidth - 48)
        const scale = Math.min(
          Math.max(targetWidth / baseViewport.width, 0.5),
          1.8,
        )
        const viewport = page.getViewport({ scale })
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!canvas || !context) return

        const deviceScale = Math.min(window.devicePixelRatio || 1, 2)
        canvas.width = Math.floor(viewport.width * deviceScale)
        canvas.height = Math.floor(viewport.height * deviceScale)
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`

        renderTask = page.render({
          canvasContext: context,
          viewport,
          transform:
            deviceScale === 1
              ? undefined
              : [deviceScale, 0, 0, deviceScale, 0, 0],
        })

        await renderTask.promise
        if (!cancelled) setIsRendering(false)
      } catch (error) {
        if (!cancelled) {
          logger.error(`Failed to render PDF page ${pageNumber}:`, error)
          setIsRendering(false)
        }
      }
    }

    if (availableWidth > 0) void renderPage()

    return () => {
      cancelled = true
      renderTask?.cancel()
    }
  }, [availableWidth, pageNumber, pdf])

  return (
    <div className="relative flex justify-center">
      {isRendering ? (
        <div className="absolute inset-x-0 top-10 z-10 flex justify-center">
          <div className="flex items-center gap-2 rounded-md border border-border/70 bg-background px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
            <SpinnerGapIcon className="h-3.5 w-3.5 animate-spin" />
            Rendering page {pageNumber}
          </div>
        </div>
      ) : null}
      <canvas
        ref={canvasRef}
        className="max-w-full rounded-sm bg-white shadow-sm ring-1 ring-border/70"
      />
    </div>
  )
}

function PdfCanvasPreview({ data }: { data: ArrayBuffer }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const updateWidth = () => setContainerWidth(element.clientWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    let loadedPdf: PDFDocumentProxy | null = null
    const loadingTask = getDocument({
      data: new Uint8Array(data.slice(0)),
    })

    setPdf(null)
    setLoadError(null)

    async function loadPdf() {
      try {
        loadedPdf = await loadingTask.promise
        if (!cancelled) setPdf(loadedPdf)
      } catch (error) {
        if (!cancelled) {
          logger.error('Failed to load PDF preview:', error)
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Unable to render this PDF.',
          )
        }
      }
    }

    void loadPdf()

    return () => {
      cancelled = true
      void loadingTask.destroy()
      void loadedPdf?.cleanup()
    }
  }, [data])

  return (
    <div ref={containerRef} className="h-full bg-muted/30">
      {!pdf && !loadError ? (
        <div className="flex h-full items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <SpinnerGapIcon className="h-4 w-4 animate-spin" />
            Loading PDF...
          </div>
        </div>
      ) : null}

      {loadError ? (
        <div className="flex h-full items-center justify-center p-6 text-center">
          <div className="max-w-sm space-y-3">
            <WarningCircleIcon className="mx-auto h-8 w-8 text-destructive" />
            <div>
              <h3 className="font-medium">PDF preview unavailable</h3>
              <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
            </div>
          </div>
        </div>
      ) : null}

      {pdf ? (
        <ScrollArea className="h-full">
          <div className="space-y-4 p-4">
            {Array.from({ length: pdf.numPages }, (_, index) => (
              <PdfPageCanvas
                key={index + 1}
                pdf={pdf}
                pageNumber={index + 1}
                availableWidth={containerWidth}
              />
            ))}
          </div>
        </ScrollArea>
      ) : null}
    </div>
  )
}

function renderPreviewContent(
  state: PreviewState,
  options: {
    isLinkPreview?: boolean
    isTextFallbackLoading?: boolean
    onOpenExternal?: () => void
    onViewAsText?: () => void
  } = {},
) {
  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <SpinnerGapIcon className="h-4 w-4 animate-spin" />
          Loading preview...
        </div>
      </div>
    )
  }

  if (state.status === 'error') {
    if (options.isLinkPreview && options.onOpenExternal) {
      return <OpenLinkFallback onOpenExternal={options.onOpenExternal} />
    }

    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <div className="max-w-sm space-y-3">
          <WarningCircleIcon className="mx-auto h-8 w-8 text-destructive" />
          <div>
            <h3 className="font-medium">Preview unavailable</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {state.message}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (state.kind === 'image' && state.url) {
    return (
      <div className="flex h-full items-center justify-center p-6 sm:p-10">
        <img
          src={state.url}
          alt=""
          className="max-h-full max-w-full rounded-xl object-contain shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75)] ring-1 ring-white/10"
        />
      </div>
    )
  }

  if (state.kind === 'video' && state.url) {
    return (
      <div className="flex h-full items-center justify-center bg-black">
        <video
          src={state.url}
          controls
          className="h-full max-h-full max-w-full"
        />
      </div>
    )
  }

  if (state.kind === 'audio' && state.url) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/30 p-6">
        <audio src={state.url} controls className="w-full max-w-xl" />
      </div>
    )
  }

  if (state.kind === 'pdf' && state.pdfData) {
    return <PdfCanvasPreview data={state.pdfData} />
  }

  if (state.kind === 'pptx' && state.slides) {
    return renderSlides(state.slides)
  }

  if (state.kind === 'archive' && state.archiveEntries) {
    return renderArchiveEntries(state.archiveEntries)
  }

  if (state.kind === 'url' && state.url) {
    if (shouldOpenUrlExternally(state.url) && options.onOpenExternal) {
      return <OpenLinkFallback onOpenExternal={options.onOpenExternal} />
    }

    return (
      <UrlPreviewFrame
        url={state.url}
        onOpenExternal={options.onOpenExternal}
      />
    )
  }

  if (state.kind === 'html' && state.url) {
    return (
      <iframe
        src={state.url}
        title="HTML page preview"
        className="h-full w-full bg-background"
        sandbox="allow-downloads allow-popups allow-presentation"
      />
    )
  }

  if ((state.kind === 'docx' || state.kind === 'markdown') && state.html) {
    return (
      <ScrollArea className="h-full">
        <article
          className="prose prose-sm dark:prose-invert max-w-none p-6"
          dangerouslySetInnerHTML={{ __html: state.html }}
        />
      </ScrollArea>
    )
  }

  if (state.kind === 'csv' && state.csvRows) {
    return (
      <ScrollArea className="h-full">{renderTable(state.csvRows)}</ScrollArea>
    )
  }

  if (state.kind === 'xlsx' && state.sheetRows) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-border/70 px-4 py-2 text-xs font-medium text-muted-foreground">
          {state.sheetName}
        </div>
        <ScrollArea className="min-h-0 flex-1">
          {renderTable(state.sheetRows)}
        </ScrollArea>
      </div>
    )
  }

  if (state.kind === 'text' && typeof state.text === 'string') {
    return (
      <ScrollArea className="h-full bg-muted/20">
        <pre className="whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed">
          {state.text}
        </pre>
      </ScrollArea>
    )
  }

  if (
    state.kind === 'unsupported' &&
    options.isLinkPreview &&
    options.onOpenExternal
  ) {
    return <OpenLinkFallback onOpenExternal={options.onOpenExternal} />
  }

  return (
    <div className="flex h-full items-center justify-center p-6 text-center">
      <div className="max-w-sm space-y-3">
        <FileTextIcon className="mx-auto h-8 w-8 text-muted-foreground" />
        <div>
          <h3 className="font-medium">No in-app preview for this file type</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {state.unsupportedReason ||
              'This format is not safely previewable in the browser. You can still download it.'}
          </p>
        </div>
        {state.url && options.onViewAsText ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={options.onViewAsText}
            disabled={options.isTextFallbackLoading}
          >
            {options.isTextFallbackLoading ? (
              <SpinnerGapIcon className="h-4 w-4 animate-spin" />
            ) : (
              <FileTextIcon className="h-4 w-4" />
            )}
            View as text
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function shouldOpenUrlExternally(rawUrl: string) {
  try {
    const url = new URL(rawUrl)
    const host = url.hostname.toLowerCase()

    return (
      !['http:', 'https:'].includes(url.protocol) ||
      host === 'docs.google.com' ||
      host === 'drive.google.com'
    )
  } catch {
    return true
  }
}

function OpenLinkFallback({ onOpenExternal }: { onOpenExternal: () => void }) {
  return (
    <div className="flex h-full items-center justify-center bg-muted/20 p-6">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={onOpenExternal}
      >
        <ArrowSquareOutIcon className="h-4 w-4" />
        Open link
      </Button>
    </div>
  )
}

function UrlPreviewFrame({
  url,
  onOpenExternal,
}: {
  url: string
  onOpenExternal?: () => void
}) {
  const [loadFailed, setLoadFailed] = useState(false)
  const loadTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    setLoadFailed(false)

    loadTimeoutRef.current = window.setTimeout(() => {
      setLoadFailed(true)
    }, 10000)

    return () => {
      if (loadTimeoutRef.current != null) {
        window.clearTimeout(loadTimeoutRef.current)
        loadTimeoutRef.current = null
      }
    }
  }, [url])

  const handleLoad = () => {
    if (loadTimeoutRef.current != null) {
      window.clearTimeout(loadTimeoutRef.current)
      loadTimeoutRef.current = null
    }
    setLoadFailed(false)
  }

  if (loadFailed && onOpenExternal) {
    return <OpenLinkFallback onOpenExternal={onOpenExternal} />
  }

  return (
    <iframe
      src={url}
      title="Document preview"
      className="h-full w-full bg-background"
      sandbox="allow-downloads allow-forms allow-popups allow-presentation allow-same-origin allow-scripts"
      onLoad={handleLoad}
      onError={() => setLoadFailed(true)}
    />
  )
}

export function DocumentPreviewDialog({
  document,
  open,
  onOpenChange,
  resolveSignedUrl,
}: DocumentPreviewDialogProps) {
  const [state, setState] = useState<PreviewState>({ status: 'idle' })
  const [isTextFallbackLoading, setIsTextFallbackLoading] = useState(false)

  const resolver = useMemo(
    () =>
      resolveSignedUrl ??
      ((doc: PreviewableDocument) => documentsApi.getDocumentSignedUrl(doc.id)),
    [resolveSignedUrl],
  )

  useEffect(() => {
    if (!open || !document) {
      setState({ status: 'idle' })
      setIsTextFallbackLoading(false)
      return
    }

    let cancelled = false

    async function loadPreview() {
      setState({ status: 'loading' })
      setIsTextFallbackLoading(false)

      try {
        if (!document) return

        if (document.type === DocumentTypeEnum.URL) {
          const url = normalizeUrl(document.url)
          if (!url) {
            throw new Error('Link URL is missing')
          }
          if (!/^https?:\/\//i.test(url)) {
            setState({
              status: 'ready',
              kind: 'unsupported',
              url,
              unsupportedReason:
                'Only HTTP and HTTPS links can be previewed inside the app.',
            })
            return
          }
          setState({ status: 'ready', kind: 'url', url })
          return
        }

        const signed = await resolver(document)
        if (!signed?.url) {
          throw new Error('Signed preview URL is not available')
        }

        const kind = getPreviewKind(document)
        if (['image', 'video', 'audio'].includes(kind)) {
          if (!cancelled) setState({ status: 'ready', kind, url: signed.url })
          return
        }

        if (kind === 'pdf') {
          const pdfData = await fetchArrayBuffer(signed.url)
          if (!cancelled)
            setState({ status: 'ready', kind, pdfData, url: signed.url })
          return
        }

        if (kind === 'pptx') {
          const slides = await parsePptx(signed.url)
          if (!cancelled)
            setState({ status: 'ready', kind, slides, url: signed.url })
          return
        }

        if (kind === 'archive') {
          const archiveEntries = await parseArchive(signed.url)
          if (!cancelled) {
            setState({
              status: 'ready',
              kind,
              archiveEntries,
              url: signed.url,
            })
          }
          return
        }

        if (kind === 'text') {
          const text = await fetchText(signed.url)
          if (!cancelled)
            setState({ status: 'ready', kind, text, url: signed.url })
          return
        }

        if (kind === 'markdown') {
          const text = await fetchText(signed.url)
          const html = DOMPurify.sanitize(markdown.render(text))
          if (!cancelled)
            setState({ status: 'ready', kind, html, url: signed.url })
          return
        }

        if (kind === 'html') {
          if (!cancelled) {
            setState({ status: 'ready', kind, url: signed.url })
          }
          return
        }

        if (kind === 'csv') {
          const text = await fetchText(signed.url)
          const csvRows = parseDelimitedRows(text, document)
          if (!cancelled)
            setState({ status: 'ready', kind, csvRows, url: signed.url })
          return
        }

        if (kind === 'xlsx') {
          const { sheetName, sheetRows } = await parseXlsx(signed.url)
          if (!cancelled) {
            setState({
              status: 'ready',
              kind,
              sheetName,
              sheetRows,
              url: signed.url,
            })
          }
          return
        }

        if (kind === 'docx') {
          const html = await parseDocx(signed.url)
          if (!cancelled)
            setState({ status: 'ready', kind, html, url: signed.url })
          return
        }

        if (!cancelled) {
          setState({
            status: 'ready',
            kind: 'unsupported',
            url: signed.url,
          })
        }
      } catch (error) {
        logger.error('Failed to load document preview:', error)
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Unable to generate a preview for this document.',
          })
        }
      }
    }

    void loadPreview()

    return () => {
      cancelled = true
    }
  }, [document, open, resolver])

  const activeUrl =
    state.status === 'ready' || state.status === 'error' ? state.url : undefined
  const isLinkPreview = document?.type === DocumentTypeEnum.URL
  const extension = document ? getExtension(document) : ''
  const typeLabel = isLinkPreview
    ? 'Web link'
    : extension
      ? extension.toUpperCase()
      : document?.contentType?.split('/').pop()?.toUpperCase() || 'File'
  const sizeLabel = formatFileSize(document?.fileSize)
  const sourceLabel = (() => {
    if (!document) return ''
    if (isLinkPreview && document.url) {
      try {
        return new URL(normalizeUrl(document.url)).hostname
      } catch {
        return document.url
      }
    }
    if (document.fileName && document.fileName !== document.displayName) {
      return document.fileName
    }
    return document.contentType || ''
  })()

  const handleDownload = async () => {
    if (!document) return

    try {
      if (document.type === DocumentTypeEnum.URL) {
        const url = normalizeUrl(document.url)
        if (url) await openExternal(url)
        return
      }

      const url = activeUrl || (await resolver(document)).url
      await downloadFileWithFeedback(
        url,
        document.fileName || document.displayName || 'download',
      )
    } catch (error) {
      logger.error('Failed to download from preview:', error)
    }
  }

  const handleOpenExternal = async () => {
    if (!document) return
    const url =
      document.type === DocumentTypeEnum.URL
        ? normalizeUrl(document.url)
        : activeUrl || (await resolver(document)).url

    if (!url) return
    if (isTauri()) {
      await openExternal(url)
      return
    }
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleViewAsText = async () => {
    if (!document || document.type !== DocumentTypeEnum.FILE) return

    setIsTextFallbackLoading(true)

    try {
      const url = activeUrl || (await resolver(document)).url
      const text = await fetchText(url)
      setState({ status: 'ready', kind: 'text', text, url })
    } catch (error) {
      logger.error('Failed to preview document as text:', error)
      toast.error(
        error instanceof Error ? error.message : 'Unable to preview as text',
      )
      if (state.status === 'ready' && state.kind === 'unsupported') {
        setState({
          ...state,
          unsupportedReason:
            error instanceof Error
              ? error.message
              : 'Unable to preview this file as text.',
        })
      }
    } finally {
      setIsTextFallbackLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="h-[min(92vh,960px)] max-w-[min(1280px,calc(100vw-1.5rem))] overflow-hidden rounded-[1.35rem] border-border/60 bg-background/95 p-0 shadow-[0_36px_120px_-28px_rgba(0,0,0,0.65)] backdrop-blur-xl"
      >
        <DialogHeader className="flex-none border-b border-border/60 bg-background/90 px-4 py-3 text-left sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.9rem] bg-foreground text-background shadow-sm">
              {isLinkPreview ? (
                <ArrowSquareOutIcon className="h-5 w-5" weight="bold" />
              ) : (
                <FileTextIcon className="h-5 w-5" weight="bold" />
              )}
              <span className="absolute -bottom-1 -right-1 max-w-[3.25rem] truncate rounded-md border border-background bg-primary px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-primary-foreground shadow-sm">
                {isLinkPreview ? 'URL' : typeLabel}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <DialogTitle
                className="truncate text-[15px] font-semibold tracking-[-0.01em] sm:text-base"
                title={
                  document?.displayName ||
                  document?.fileName ||
                  'Document preview'
                }
              >
                {document?.displayName ||
                  document?.fileName ||
                  'Document preview'}
              </DialogTitle>
              <DialogDescription className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] sm:text-xs">
                <span className="shrink-0 font-semibold uppercase tracking-[0.12em] text-foreground/60">
                  {typeLabel}
                </span>
                {sizeLabel && (
                  <>
                    <span aria-hidden className="text-border">
                      /
                    </span>
                    <span className="shrink-0 tabular-nums">{sizeLabel}</span>
                  </>
                )}
                {sourceLabel && (
                  <>
                    <span aria-hidden className="text-border">
                      /
                    </span>
                    <span className="min-w-0 truncate">{sourceLabel}</span>
                  </>
                )}
              </DialogDescription>
            </div>

            <div className="flex shrink-0 items-center gap-1 rounded-xl border border-border/60 bg-muted/40 p-1 shadow-sm">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 rounded-lg px-2.5 text-xs"
                onClick={() => void handleOpenExternal()}
                disabled={!document}
                title={isLinkPreview ? 'Open link' : 'Open in new window'}
              >
                <ArrowSquareOutIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Open</span>
              </Button>
              {document?.type === DocumentTypeEnum.FILE && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5 rounded-lg px-2.5 text-xs"
                  onClick={() => void handleDownload()}
                  disabled={!document}
                  title="Download file"
                >
                  <DownloadSimpleIcon className="h-4 w-4" />
                  <span className="hidden md:inline">Download</span>
                </Button>
              )}
            </div>

            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Close preview"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>
        <div className="relative min-h-0 flex-1 overflow-hidden bg-[#dfe1e5] dark:bg-[#111315]">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.48),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.10),transparent_28%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.06),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,0.12),transparent_28%)]" />
          <div className="relative h-full min-h-0">
            {renderPreviewContent(state, {
              isLinkPreview,
              isTextFallbackLoading,
              onOpenExternal:
                document?.type === DocumentTypeEnum.URL
                  ? () => void handleOpenExternal()
                  : undefined,
              onViewAsText:
                document?.type === DocumentTypeEnum.FILE
                  ? handleViewAsText
                  : undefined,
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function DocumentPreviewButton({
  document,
  resolveSignedUrl,
  className,
  buttonClassName,
  variant = 'ghost',
  size = 'sm',
  showLabel = false,
  label = 'Preview',
  disabled = false,
  onClick,
}: DocumentPreviewButtonProps) {
  const [open, setOpen] = useState(false)

  return (
    <span className={className} onClick={event => event.stopPropagation()}>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={buttonClassName}
        title="Preview"
        aria-label="Preview document"
        disabled={disabled}
        onClick={event => {
          onClick?.(event)
          if (event.defaultPrevented) return
          setOpen(true)
        }}
      >
        <EyeIcon className={cn('h-4 w-4', showLabel && 'mr-1.5')} />
        {showLabel && label}
      </Button>
      <DocumentPreviewDialog
        document={document}
        open={open}
        onOpenChange={setOpen}
        resolveSignedUrl={resolveSignedUrl}
      />
    </span>
  )
}
